import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { AccessibilitySettings } from '../components/ui/AccessibilitySettings';
import { Navigate } from 'react-router-dom';

const Settings: React.FC = () => {
  const { user, loading } = useAuth();
  const [activeTab, setActiveTab] = useState('preferences');

  // If not authenticated, redirect to home
  if (!loading && !user) {
    return <Navigate to="/" />;
  }

  if (loading || !user) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="animate-pulse flex flex-col space-y-4">
          <div className="h-12 bg-gray-800 rounded-xl w-1/3"></div>
          <div className="h-64 bg-gray-800 rounded-xl w-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <h1 className="text-3xl font-outfit font-bold text-white mb-6">Settings</h1>
      
      <div className="bg-[#121216] border border-gray-800 rounded-xl overflow-hidden shadow-lg">
        {/* Tabs */}
        <div className="border-b border-gray-800">
          <nav className="flex">
            <button
              onClick={() => setActiveTab('preferences')}
              className={`px-6 py-4 text-sm font-medium ${
                activeTab === 'preferences'
                  ? 'text-[#FF4D8C] border-b-2 border-[#FF4D8C]'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Preferences
            </button>
            <button
              onClick={() => setActiveTab('security')}
              className={`px-6 py-4 text-sm font-medium ${
                activeTab === 'security'
                  ? 'text-[#FF4D8C] border-b-2 border-[#FF4D8C]'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Security
            </button>
          </nav>
        </div>
        
        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'preferences' && (
            <div>
              <h3 className="text-xl font-medium text-white mb-4">Your Preferences</h3>
              
              <div className="space-y-6">
                <AccessibilitySettings />
              </div>
            </div>
          )}
          
          {activeTab === 'security' && (
            <div>
              <h3 className="text-xl font-medium text-white mb-4">Security Settings</h3>
              
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-medium text-white mb-2">Login Sessions</h4>
                  <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white">Current Session</p>
                        <p className="text-gray-400 text-sm">This device • Signed in with Google</p>
                      </div>
                      <span className="px-2 py-1 bg-green-900/30 text-green-400 text-xs rounded-lg">
                        Active
                      </span>
                    </div>
                  </div>
                  <p className="text-gray-400 text-sm mt-2">
                    Your account is secured with Google OAuth. You can manage your Google account security settings directly through Google.
                  </p>
                </div>

                <div>
                  <h4 className="text-lg font-medium text-white mb-2">Account Security</h4>
                  <div className="space-y-4">
                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium">Two-Factor Authentication</p>
                          <p className="text-gray-400 text-sm">Managed by Google Account</p>
                        </div>
                        <a
                          href="https://myaccount.google.com/security"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 bg-naia-pink hover:bg-pink-500 text-white rounded-lg transition-colors text-sm"
                        >
                          Configure
                        </a>
                      </div>
                    </div>

                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium">Password Management</p>
                          <p className="text-gray-400 text-sm">Change your Google account password</p>
                        </div>
                        <a
                          href="https://myaccount.google.com/security"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
                        >
                          Manage
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-medium text-white mb-2">Data & Privacy</h4>
                  <div className="space-y-4">
                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium">Download Your Data</p>
                          <p className="text-gray-400 text-sm">Export your playlists, favorites, and listening history</p>
                        </div>
                        <button className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm">
                          Request Export
                        </button>
                      </div>
                    </div>

                    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium">Delete Account</p>
                          <p className="text-gray-400 text-sm">Permanently delete your Naia account and data</p>
                        </div>
                        <button className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm">
                          Delete Account
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
